import { MillenniumDate } from "./MillenniumDate.js";
import { MillenniumDateTime } from "./MillenniumDateTime.js";
import { MillenniumWeekday } from "./MillenniumWeekday.js";
export class MillenniumWeek {
    static UNDEFINED_WEEK_NUMBER = 0;
    static MIN_WEEK_NUMBER = 1;
    static MAX_WEEK_NUMBER = 6752;
    date;
    firstDayOfWeek;
    daysInFirstWeek;
    _week;
    _weekWithYear;
    constructor(aDateInWeek, firstDayOfWeek, daysInFirstWeek) {
        this.firstDayOfWeek = firstDayOfWeek;
        this.daysInFirstWeek = daysInFirstWeek;
        this.date = new MillenniumDate(this.getFirstDayNumberOfWeek(aDateInWeek));
        let keyDayNumber = this.date.getDayNumber();
        const dFW = daysInFirstWeek;
        if (keyDayNumber !== MillenniumDate.UNDEFINED_DAY_NUMBER &&
            keyDayNumber + 6 <= MillenniumDate.MAX_DAY_NUMBER) {
            keyDayNumber += 7 - dFW;
            const year = new MillenniumDate(keyDayNumber).getYear();
            const keyDayNumberFirstWeeek = this.getFirstDayNumberOfWeek(MillenniumDate.create(year, 1, dFW + 7)) - dFW;
            const week = Math.floor((keyDayNumber - keyDayNumberFirstWeeek) / 7) + 1;
            this._week = week; // Week without year
            this._weekWithYear = String((year % 100) * 100 + week);
            if (this._weekWithYear.length < 4) {
                this._weekWithYear = `0${this._weekWithYear}`;
            }
        }
        else {
            this._week = MillenniumWeek.UNDEFINED_WEEK_NUMBER;
            this._weekWithYear = "0";
        }
    }
    static create(weekNumbers, firstDayOfWeek, daysInFirstWeek) {
        let returnArray = true;
        if (!Array.isArray(weekNumbers)) {
            weekNumbers = [weekNumbers];
            returnArray = false;
        }
        const result = [];
        const numWeeks = weekNumbers.length;
        let i, weekNumber;
        const tempWeek = new MillenniumWeek(MillenniumDate.today(), firstDayOfWeek, daysInFirstWeek);
        for (i = 0; i < numWeeks; i++) {
            weekNumber = weekNumbers[i];
            weekNumber = parseInt(weekNumber, 10);
            if (weekNumber > 200000) {
                weekNumber = weekNumber - 200000;
            }
            result.push(new MillenniumWeek(new MillenniumDate(tempWeek.weekNumberToDayNumber(weekNumber)), firstDayOfWeek, daysInFirstWeek));
        }
        return returnArray ? result : result[0];
    }
    static today(firstDayOfWeek, daysInFirstWeek) {
        return new MillenniumWeek(MillenniumDate.today(), firstDayOfWeek, daysInFirstWeek);
    }
    static toString(weeks, formatter = defaultFormatter) {
        if (!Array.isArray(weeks)) {
            return weeks.week();
        }
        let prevWeek = null;
        const groupedWeeks = [];
        weeks.forEach((week) => {
            if (prevWeek && prevWeek.week() === week.addWeeks(-1).week()) {
                const prevPeriod = groupedWeeks[groupedWeeks.length - 1];
                if (Array.isArray(prevPeriod)) {
                    prevPeriod.push(week);
                }
                else {
                    groupedWeeks[groupedWeeks.length - 1] = [prevPeriod, week];
                }
            }
            else {
                // It used to be this. But it's probably wrong?? -> groupedWeeks.push(week);
                groupedWeeks.push([week]);
            }
            prevWeek = week;
        });
        return groupedWeeks
            .map(formatter)
            .filter((str) => Boolean(str))
            .join(", ");
    }
    week(includeYear) {
        return this.getWeek(includeYear);
    }
    getWeek(includeYear = true) {
        return includeYear ? this._weekWithYear : this._week;
    }
    isSameWeekAs(other) {
        return this.week() === other.week();
    }
    addWeeks(num) {
        return new MillenniumWeek(this.date.addDays(num * 7), this.firstDayOfWeek, this.daysInFirstWeek);
    }
    weekNumberToDayNumber(weekNumber) {
        if (weekNumber >= MillenniumWeek.MIN_WEEK_NUMBER &&
            weekNumber <= MillenniumWeek.MAX_WEEK_NUMBER) {
            const year = Math.floor(weekNumber / 100);
            const week = weekNumber % 100;
            if (week === 1) {
                return this.getFirstDayNumberOfWeek(MillenniumDate.create(year, 1, this.getDaysInFirstWeek()));
            }
            let dayNumber = this.getFirstDayNumberOfWeek(MillenniumDate.create(year, 1, this.getDaysInFirstWeek() + 7));
            if (dayNumber !== MillenniumDate.UNDEFINED_DAY_NUMBER) {
                dayNumber += (week - 2) * 7;
                return dayNumber;
            }
        }
        return MillenniumDate.UNDEFINED_DAY_NUMBER;
    }
    getStartDate() {
        return this.date;
    }
    /**
     * Return the date and time for the start of the week.
     *
     * @return {MillenniumDateTime} start of week
     */
    getStartOfWeek() {
        return MillenniumDateTime.fromDate(this.date);
    }
    getDateForWeekday(weekday) {
        const startOfWeek = this.getStartOfWeek();
        startOfWeek?.getMillenniumDate();
        let dateNumber = this.getStartOfWeek().getMillenniumDate().getDayNumber();
        dateNumber = dateNumber - this.firstDayOfWeek.getAbsoluteWeekdayNumber();
        dateNumber = dateNumber + weekday.getAbsoluteWeekdayNumber();
        return new MillenniumDate(dateNumber);
    }
    getFirstDayNumberOfWeek(day) {
        if (!day) {
            return MillenniumDate.UNDEFINED_DAY_NUMBER;
        }
        if (day instanceof MillenniumDateTime) {
            day = day.getMillenniumDate();
        }
        if (!(day instanceof MillenniumDate)) {
            day = new MillenniumDate(day);
        }
        const dayNumber = day.getDayNumber();
        const weekdayNumber = this.getWeekdayNumber(day);
        if (dayNumber >= weekdayNumber) {
            return dayNumber - weekdayNumber;
        }
        return MillenniumDate.UNDEFINED_DAY_NUMBER;
    }
    getDayOfWeek(date) {
        return (date.getDayNumber() + MillenniumWeekday.SATURDAY.getAbsoluteWeekdayNumber()) % 7;
    }
    dayOfWeek2WeekdayNumber(dayOfWeek) {
        return (dayOfWeek + 7 - this.getFirstDayOfWeek().getAbsoluteWeekdayNumber()) % 7;
    }
    getWeekdayNumber(aDate) {
        return this.dayOfWeek2WeekdayNumber(this.getDayOfWeek(aDate));
    }
    getDaysInFirstWeek() {
        return this.daysInFirstWeek;
    }
    getFirstDayOfWeek() {
        return this.firstDayOfWeek;
    }
    weeksBetween(otherWeek) {
        return Math.abs(Math.floor((this.date.getDayNumber() - otherWeek.date.getDayNumber()) / 7));
    }
    equals(obj) {
        if (this === obj) {
            return true;
        }
        if (obj === null || obj === undefined) {
            return false;
        }
        if (!(obj instanceof MillenniumWeek)) {
            return false;
        }
        return this.getWeek(true) === obj.getWeek(true);
    }
}
const defaultFormatter = (period) => {
    if (Array.isArray(period)) {
        return `${period[0].week()} - ${period[period.length - 1].week()}`;
    }
    return period.week();
};
